import { Suspense, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ReactFlow, { PanOnScrollMode, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import { useMutation } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import * as Tabs from '@givebutter/primitives/tabs';
import { useToast } from '@givebutter/primitives/toast';
import {
    faArrowRight,
    faClose,
    faInfoCircle,
    faSpinnerThird,
} from '@fortawesome/pro-regular-svg-icons';
import { faArrowProgress, faBolt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createWorkflow, updateWorkflowSteps } from 'api/workflows';
import * as BuilderFrame from 'assets/js/pages/engage/workflows/components/BuilderFrame';
import { PreviewBadge } from 'assets/js/pages/engage/workflows/components/PreviewBadge';
import { SmallBrowserAlertDialog } from 'assets/js/pages/engage/workflows/components/SmallBrowserAlertDialog';
import { WorkflowStarterItem } from 'assets/js/pages/engage/workflows/components/WorkflowStarterItem';
import { useEngageMessageTemplates } from 'assets/js/pages/engage/workflows/hooks/useEngageMessageTemplates';
import { useLayout } from 'assets/js/pages/engage/workflows/hooks/useLayout';
import { useSetViewportCenter } from 'assets/js/pages/engage/workflows/hooks/useSetViewportCenter';
import { SelectedPreset } from 'assets/js/pages/engage/workflows/types';
import { getNodesFromSelectedPreset } from 'assets/js/pages/engage/workflows/utils/getNodesFromSelectedPreset';
import { nodeTypes } from 'assets/js/pages/engage/workflows/utils/nodeTypes';
import {
    enabledTriggers,
    enabledWorkflows,
    givebutterDataNoticeSlugs,
} from 'assets/js/pages/engage/workflows/utils/starterItems';
import { allSelector, useReactFlowStore } from 'assets/js/pages/engage/workflows/utils/store';
import { transformNodesToSteps } from 'assets/js/pages/engage/workflows/utils/transformers';
import { NodeWithData } from 'assets/js/pages/engage/workflows/utils/types';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { queryClient } from 'queries/queryClient';
import { workflowQueryKeys } from 'queries/workflow-query-keys';
import { twJoin } from 'tailwind-merge';
import { Workflow, WorkflowStep } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';
import { useShallow } from 'zustand/react/shallow';

interface CreateWorkflowMutationFnResponse {
    workflow: Workflow;
    workflowSteps: WorkflowStep[];
}

const CreateWorkflow = () => {
    const { error } = useToast();
    const { accountId } = useCurrentAccount();
    const history = useHistory();
    const [selectedPreset, setSelectedPreset] = useState<SelectedPreset>('');
    const selectedItem =
        enabledWorkflows.find(w => w.slug === selectedPreset) ||
        enabledTriggers.find(t => t.slug === selectedPreset);
    const showGivebutterDataNotice =
        selectedItem && givebutterDataNoticeSlugs.includes(selectedItem.slug);

    const { nodes } = useReactFlowStore(({ nodes }) => ({ nodes }));

    const { data: messageTemplates } = useEngageMessageTemplates();

    const availableWorkflows = enabledWorkflows.filter(workflow => {
        const availableMessageTemplates = messageTemplates.map(t => t.slug);
        return workflow.messageTemplateSlug
            ? availableMessageTemplates.includes(workflow.messageTemplateSlug)
            : true;
    });

    const { mutate: submit, isPending } = useMutation<
        CreateWorkflowMutationFnResponse,
        GivebutterAxiosError,
        { nodes: NodeWithData[] }
    >({
        mutationFn: async ({ nodes }) => {
            const [triggerNode] = nodes;
            const trigger = enabledTriggers.find(t => t.slug === triggerNode.type);

            const { id, updated_at } = await createWorkflow(accountId, {
                trigger: trigger!.slug,
                name: selectedItem!.name,
                icon: selectedItem!.icon,
                execute_once: trigger!.reEnrollment?.initialValue,
            });

            const {
                data: { steps: workflowSteps, workflow },
            } = await updateWorkflowSteps(accountId, id, {
                steps: transformNodesToSteps({ nodes, workflowId: id }),
                workflow_updated_at: updated_at,
            });

            return { workflow, workflowSteps };
        },
        onSuccess: ({ workflow, workflowSteps }) => {
            queryClient.invalidateQueries({ queryKey: workflowQueryKeys.workflows(accountId) });
            queryClient.setQueryData(
                workflowQueryKeys.workflowById(accountId, workflow.id),
                workflow,
            );
            queryClient.setQueryData(
                workflowQueryKeys.workflowStepsByIdWithFilters(accountId, workflow.id, {
                    version: workflow.current_version,
                }),
                workflowSteps,
            );
            history.push(`./${workflow.id}/build`);
        },
        onError: e => {
            error(e.response?.data.message || 'Failed to create workflow. Please try again.');
        },
    });

    return (
        <>
            <SmallBrowserAlertDialog />
            <BuilderFrame.Root>
                <BuilderFrame.Title>Create new workflow</BuilderFrame.Title>
                <BuilderFrame.Header>
                    <div className="flex items-center gap-6">
                        <Button
                            onClick={() => history.push(`/accounts/${accountId}/workflows`)}
                            variant="transparent"
                            size="sm"
                            shape="square"
                            color="neutral"
                            aria-label="Close workflow builder"
                        >
                            <FontAwesomeIcon icon={faClose} size="lg" />
                        </Button>
                        <p className="text-lg font-medium">How do you want to start?</p>
                    </div>
                    {selectedPreset ? (
                        <Button
                            variant="solid"
                            disabled={isPending || !selectedPreset}
                            onClick={() => {
                                if (!selectedPreset) return;
                                submit({ nodes });
                            }}
                        >
                            Start building
                            {isPending ? (
                                <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin" />
                            ) : (
                                <FontAwesomeIcon icon={faArrowRight} />
                            )}
                        </Button>
                    ) : null}
                </BuilderFrame.Header>
                <BuilderFrame.Content className="flex">
                    <Tabs.Root
                        defaultValue="workflows"
                        variant={'underline'}
                        className="flex h-full min-h-0 w-1/2 max-w-screen-md flex-col border-r border-r-gray-200 bg-white"
                    >
                        <Tabs.List>
                            <Tabs.Trigger
                                value="workflows"
                                className="radix-state-active:bg-blue-25 text-gray-500"
                            >
                                <FontAwesomeIcon
                                    icon={faArrowProgress}
                                    className="group-radix-state-active:text-blue-500 text-gray-400"
                                />
                                Workflows
                            </Tabs.Trigger>
                            <Tabs.Trigger
                                value="triggers"
                                className="radix-state-active:bg-blue-25 text-gray-500"
                            >
                                <FontAwesomeIcon
                                    icon={faBolt}
                                    className="group-radix-state-active:text-blue-500 text-gray-400"
                                />
                                Triggers
                            </Tabs.Trigger>
                        </Tabs.List>
                        <Tabs.Content value="workflows" className="h-full overflow-y-auto p-6">
                            <ul className="flex flex-col gap-4">
                                {availableWorkflows.map(workflow => (
                                    <WorkflowStarterItem
                                        key={workflow.slug}
                                        item={workflow}
                                        isSelected={workflow.slug === selectedPreset}
                                        setSelectedPreset={setSelectedPreset}
                                    />
                                ))}
                            </ul>
                        </Tabs.Content>
                        <Tabs.Content value="triggers" className="h-full overflow-y-auto p-6">
                            <ul className="flex flex-col gap-4">
                                {enabledTriggers.map(trigger => (
                                    <WorkflowStarterItem
                                        key={trigger.slug}
                                        item={trigger}
                                        isSelected={trigger.slug === selectedPreset}
                                        setSelectedPreset={setSelectedPreset}
                                    />
                                ))}
                            </ul>
                        </Tabs.Content>
                    </Tabs.Root>

                    <div className="relative flex flex-grow items-center justify-center bg-gray-100 shadow-inner">
                        <div
                            className={twJoin(
                                'border-g-200 absolute top-4 z-20 flex items-center gap-4 rounded-full border bg-white bg-opacity-80 py-4 pl-6 pr-8 shadow-sm',
                            )}
                        >
                            <PreviewBadge className="flex-shrink-0 rounded-full px-4 py-2.5" />
                            <p className="text-base text-gray-600">
                                This is only a preview. Start building to customize its contents!
                            </p>
                        </div>

                        {showGivebutterDataNotice && (
                            <Box className="absolute bottom-4 z-20 p-4">
                                <div className="flex items-center gap-4">
                                    <BoxIcon size="md">
                                        <FontAwesomeIcon icon={faInfoCircle} />
                                    </BoxIcon>
                                    <div className="flex flex-col">
                                        <p className="text-sm font-medium">
                                            Based on data in your Givebutter account
                                        </p>
                                        <p className="text-sm">
                                            This criteria is only based on gifts imported to or
                                            processed through Givebutter.
                                        </p>
                                    </div>
                                </div>
                            </Box>
                        )}

                        <ReactFlowProvider>
                            <CreateWorkflowPreview selected={selectedPreset} />
                        </ReactFlowProvider>
                    </div>
                </BuilderFrame.Content>
            </BuilderFrame.Root>
        </>
    );
};

const CreateWorkflowPreview = ({ selected }: { selected: SelectedPreset }) => {
    const selectedItem =
        enabledWorkflows.find(w => w.slug === selected) ||
        enabledTriggers.find(t => t.slug === selected);

    const { data: messageTemplates } = useEngageMessageTemplates();

    const { nodes, edges, onNodesChange, onEdgesChange, setNodes, setEdges } = useReactFlowStore(
        useShallow(allSelector),
    );

    useSetViewportCenter();
    useLayout();

    useEffect(() => {
        if (!selectedItem) {
            setNodes([]);
            return;
        }

        const newNodes = getNodesFromSelectedPreset(selectedItem, messageTemplates);
        setNodes(newNodes);
    }, [selectedItem, messageTemplates, setNodes, setEdges]);

    return (
        <div className="z-10 h-full w-full">
            <Suspense fallback={<Loader />}>
                <ReactFlow
                    nodeTypes={nodeTypes}
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    proOptions={{ hideAttribution: true }}
                    panOnScroll={true}
                    panOnScrollMode={PanOnScrollMode.Vertical}
                    zoomOnScroll={false}
                    nodesDraggable={false}
                    autoPanOnNodeDrag={false}
                    zoomOnDoubleClick={false}
                />
            </Suspense>
        </div>
    );
};

export { CreateWorkflow };
